/**
 * OAuthPopulatedDropdownField -- Dynamic dropdown populated from an API
 * endpoint after dependent fields (e.g. OAuth credentials) are available.
 *
 * Behavior:
 *   - Disabled with a placeholder option until all ``depends_on`` fields are
 *     populated in the form.
 *   - Once dependencies are satisfied, POSTs to ``dropdown_config.fetch_endpoint``
 *     with a body built from ``request_body_map`` (supports dot-path lookups
 *     into form values, e.g. ``oauth_connect.refresh_token``).
 *   - Shows loading state while fetching.
 *   - Shows an error alert if the fetch fails.
 *   - If the response returns zero items, renders a single disabled
 *     "No accessible customers" option.
 *   - On success, renders a shadcn/ui Select populated with
 *     ``{ value: item[value_key], label: item[label_key] ?? item[value_key] }``.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, HelpCircle, Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api/client';
import type { DropdownConfig } from '@/types/connector-schema';
import {
  buildStoredCredsBody,
  duplicateLabelValues,
  errorMessageForCode,
  extractErrorCode,
  normalizeSelection,
  parseDiscoveryOptions,
  resolvePath,
  satisfiesDependency,
} from '@/lib/oauth/discovery';
import {
  useReportDiscoveryPending,
  useReportSubmitBlocked,
} from '@/lib/oauth/discoveryGate';
import type { DynamicFieldProps } from './index';

interface Option {
  value: string;
  label: string;
}

/** Cheap value-equality check used to decide whether auto-select needs to
 *  push a new value into the form. Arrays compared element-wise (order
 *  matters, matching the order the backend returns + the order the form
 *  stores). Anything else falls back to `===`. See #187. */
function shallowEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }
  return false;
}

export function OAuthPopulatedDropdownField({
  field,
  value,
  onChange,
  onBatchFieldChange,
  error,
  allValues,
  readOnly,
  connectorType,
  connectorName,
}: DynamicFieldProps) {
  const selectId = `field-${field.name}`;
  const helpId = `${selectId}-help`;
  const errorId = `${selectId}-error`;
  const requiredId = `${selectId}-required`;

  const config = field.dropdown_config as DropdownConfig | undefined;

  const [options, setOptions] = useState<Option[]>([]);
  const [rawItems, setRawItems] = useState<Record<string, unknown>[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const storedCredsMode = Boolean(connectorName && connectorType);

  // Check whether all dependencies are populated. The rules — credential deps
  // resolved server-side in stored-creds mode, and an open-ended `[]` counting
  // as satisfied (#1264) — live in ONE place: satisfiesDependency (#1289).
  const dependenciesReady = Boolean(
    config &&
      config.depends_on.every((name) =>
        satisfiesDependency(name, config, allValues, storedCredsMode),
      ),
  );

  // Build a stable cache key so we only refetch when deps actually change.
  // In stored-creds mode, the credential dep often resolves to null in form
  // state (server has it, form doesn't), so the dep array alone wouldn't change
  // when the user switches between configured connectors. Fold connector_name
  // + storedCredsMode into the signature so a connector switch invalidates the
  // cached fetch.
  const depsSignature = config
    ? JSON.stringify([
        storedCredsMode,
        connectorName ?? null,
        ...config.depends_on.map((n) => allValues[n] ?? null),
      ])
    : '';

  const lastFetchedSignatureRef = useRef<string | null>(null);
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    if (!config) return;
    if (!dependenciesReady) return;
    if (lastFetchedSignatureRef.current === depsSignature) return;

    lastFetchedSignatureRef.current = depsSignature;
    const mySeq = ++fetchSeqRef.current;

    const fetchOptions = async () => {
      setIsLoading(true);
      setFetchError(null);
      try {
        // Build request body from request_body_map. In stored-creds mode the
        // server resolves credentials itself, so use the shared body assembler
        // (sets connector_name + non-credential deps). In wizard/editor mode the
        // form holds the credentials, so send every mapped key as-is.
        let body: Record<string, unknown>;
        if (storedCredsMode) {
          body = buildStoredCredsBody(config, connectorName as string, allValues);
        } else {
          body = {};
          for (const [bodyKey, fieldPath] of Object.entries(config.request_body_map)) {
            body[bodyKey] = resolvePath(allValues, fieldPath);
          }
        }

        const response = await apiClient.post(config.fetch_endpoint, body);
        // A newer effect run has superseded us — drop the result.
        if (mySeq !== fetchSeqRef.current) return;

        const data = response.data;
        const items = resolvePath(data, config.response_path);
        if (!Array.isArray(items)) {
          setOptions([]);
          setHasFetched(true);
          return;
        }

        setRawItems(items.map((item) => item as Record<string, unknown>));
        setOptions(parseDiscoveryOptions(data, config));
        setHasFetched(true);
      } catch (err) {
        if (mySeq !== fetchSeqRef.current) return;
        const errorCode = extractErrorCode(err);
        setFetchError(errorMessageForCode(errorCode, err, { reconnectHint: 'above' }));
        setOptions([]);
        setHasFetched(true);
      } finally {
        // Only clear loading if no newer fetch superseded us — otherwise the
        // newer fetch's own setIsLoading(true) would be clobbered.
        if (mySeq === fetchSeqRef.current) {
          setIsLoading(false);
        }
      }
    };

    void fetchOptions();
  }, [config, dependenciesReady, depsSignature, allValues, storedCredsMode, connectorName]);

  // Report discovery-pending to the surrounding DiscoveryGate so the wizard's
  // Continue button / the editor's Save button stay disabled until the headless
  // auto_select value is committed (#34). Only auto_select fields are gated --
  // a non-auto_select dropdown requires an explicit pick and is already covered
  // by required-field validation. Pending spans the moment dependencies are
  // satisfied (fetch kicks off) until the fetch resolves, and clears when
  // `hasFetched` flips — independent of whether a value is written into form
  // state. (Since #1014 the auto_select="all" + multi branch writes nothing at
  // all on a field flagged `empty_means_all` — there the seed is display-only.
  // #1077.)
  const isPendingDiscovery = Boolean(
    config?.auto_select && dependenciesReady && (isLoading || !hasFetched),
  );
  useReportDiscoveryPending(field.name, isPendingDiscovery);

  const currentValue = typeof value === 'string' ? value : '';

  // Auto-select: fire exactly once after the first successful fetch.
  // Uses a ref gate instead of checking currentValue to avoid re-render loops
  // (onChange/onBatchFieldChange are not memoized by the parent form).
  const autoSelectFiredRef = useRef(false);
  const [autoSelectLabel, setAutoSelectLabel] = useState<string | null>(null);

  // Interactive selection state for the auto_select="all" + multi mode. Seeded
  // once after the first successful fetch (from the saved value in stored-creds
  // edit mode, else the auto_select default), then user-controlled. `null` means
  // "not yet seeded" so the interactive render shows the freshly-fetched options
  // until the seeding effect runs. #189
  const [checkedIds, setCheckedIds] = useState<string[] | null>(null);
  const [filter, setFilter] = useState('');

  // #1028: true only between clicking "Extract everything..." and the next hand
  // edit, so the operator who just acted gets positive feedback that the change
  // landed in form state. Deliberately NOT a permanent indicator for an
  // open-ended value: post-#1014 every auto_select="all" field's help_text
  // already carries the "including any added later" clause.
  const [justNormalized, setJustNormalized] = useState(false);

  const optionOrder = useMemo(() => options.map((o) => o.value), [options]);

  // #1077: whether an EMPTY stored list is a legal open-ended sentinel for THIS
  // field. #1014 assumed it was true of every `auto_select: "all"` field; at
  // the time it held for only 4 of the 10. #1093 closed the gap for the other
  // 6 (google_analytics property_ids, amazon_dsp advertiser_ids, twitter_ads
  // account_ids, reddit_ads account_ids, tiktok_ads advertiser_ids, meta_ads
  // ad_account_ids): their adapters now discover every accessible resource
  // when the stored list is empty -- ephemerally, never persisting what they
  // discover back into the configuration -- and their settings models dropped
  // `min_length=1`, so persisting [] is accepted rather than a 422. The
  // backend still says which is which via `empty_means_all`; every
  // empty-means-all behavior below is gated on this instead of on
  // `auto_select` alone.
  //
  // #1171: as of #1093 that migration is COMPLETE -- `connector_schemas.py`
  // carries 10 `auto_select: "all"` fields and 10 `empty_means_all: true`, so
  // no shipping field currently takes the unflagged branch of this gate. The
  // gate stays because the flag, not `auto_select`, is the contract: a future
  // auto_select field whose adapter cannot discover ephemerally must be able
  // to opt out. Read the unflagged cases below as forward-looking, not as a
  // description of live connectors.
  const emptyIsOpenEnded = config?.auto_select === 'all' && config?.empty_means_all === true;

  // The selection the interactive checkbox list actually renders. Before the
  // seeding effect commits `checkedIds`, derive the same seed (saved subset in
  // stored-creds edit mode, else the auto_select default) so the checkboxes
  // render in the correct state immediately — no unchecked flash and no race
  // between rendering and the seed effect. #189
  //
  // Hoisted out of renderInteractiveMulti (#1013) because the required-empty
  // gate below is a hook and cannot live inside a render helper.
  const effectiveSelection = useMemo(() => {
    if (checkedIds) return checkedIds;
    // Without auto_select the field is an operator-curated ALLOW-LIST: the
    // saved selection is the only seed, an empty one is meaningful for an
    // OPTIONAL field ("extract everything"), and there is no seeding effect to
    // defer to — so read the persisted value in wizard mode too, tolerating a
    // legacy CSV string. #938
    const persisted = config?.auto_select
      ? storedCredsMode && Array.isArray(value)
        ? (value as unknown[]).filter((v): v is string => typeof v === 'string')
        : []
      : normalizeSelection(value);
    if (persisted.length > 0) return persisted;
    // #2100: on an open-ended field nothing is pre-checked. "Everything" is the
    // explicit All-scope radio, not N checked boxes — so the first click in
    // "Only selected" mode SELECTS a property rather than unchecking it.
    return config?.auto_select === 'all' && !emptyIsOpenEnded ? optionOrder : [];
  }, [checkedIds, config, storedCredsMode, value, optionOrder, emptyIsOpenEnded]);

  // #2100: on an `empty_means_all` field the operator picks the SCOPE with a
  // distinct, labelled control -- "all, including any added later" (persists
  // the open-ended []) or "only the selected" (persists exactly the checked
  // ids, even when every one is checked). Before this the open-ended state
  // rendered as N checked boxes: clicking a property UNCHECKED it and saved the
  // other N-1, and "every box checked" silently became []. `null` = not chosen
  // this session, so the mode follows the stored value: [] (or nothing) is All.
  //
  // #2190: a THIRD scope, 'except' -- "the selected, plus any added later". The
  // field stays [] (open-ended) and the UNCHECKED ids persist to the hidden
  // sibling named by `excluded_field`, so the adapter pulls everything except
  // them, accounts discovered later included. The deny-list is stored, never
  // the checked set: "checked at save time" would collapse into an allow-list.
  // Offered only where the sibling exists and both keys can be written in one
  // batch.
  const excludedFieldName =
    emptyIsOpenEnded && onBatchFieldChange ? config?.excluded_field : undefined;
  const storedExcludedRaw = excludedFieldName ? allValues[excludedFieldName] : undefined;
  const storedExcluded = useMemo(
    () => normalizeSelection(storedExcludedRaw),
    [storedExcludedRaw],
  );
  const [excludedIds, setExcludedIds] = useState<string[] | null>(null);
  const effectiveExcluded = excludedIds ?? storedExcluded;
  const [scopeChoice, setScopeChoice] = useState<'all' | 'selected' | 'except' | null>(null);
  const storedIsOpenEnded = !(
    Array.isArray(value) && (value as unknown[]).some((v) => typeof v === 'string')
  );
  const scopeMode: 'all' | 'selected' | 'except' | null = emptyIsOpenEnded
    ? (scopeChoice ??
      (!storedIsOpenEnded ? 'selected' : storedExcluded.length > 0 ? 'except' : 'all'))
    : null;
  // In 'except' the checked boxes are every discovered option not excluded --
  // so an account discovered after the save renders checked, as it will run.
  const exceptSelection = useMemo(() => {
    const drop = new Set(effectiveExcluded);
    return optionOrder.filter((id) => !drop.has(id));
  }, [effectiveExcluded, optionOrder]);
  // The selection to restore when the operator goes All -> Only selected.
  const lastScopedRef = useRef<string[] | null>(null);
  // The deny-list to restore when the operator leaves 'except' and comes back.
  const lastExcludedRef = useRef<string[] | null>(null);
  // The last value THIS field wrote. A different incoming `value` came from
  // outside -- the config editor's Discard, or a post-save reseed -- and the
  // editor does not remount the field, so the local scope/selection must be
  // re-derived from it or a stale "Only selected" would sit over a stored [].
  const lastWrittenRef = useRef<unknown>(value);
  // #2190: the same for the deny-list sibling.
  const lastWrittenExcludedRef = useRef<unknown>(storedExcludedRaw);
  useEffect(() => {
    if (!emptyIsOpenEnded) return;
    if (
      shallowEqual(value, lastWrittenRef.current) &&
      shallowEqual(storedExcludedRaw, lastWrittenExcludedRef.current)
    )
      return;
    lastWrittenRef.current = value;
    lastWrittenExcludedRef.current = storedExcludedRaw;
    lastScopedRef.current = null;
    lastExcludedRef.current = null;
    // Deferred like the auto-select effect: keep setState out of the effect body.
    queueMicrotask(() => {
      setScopeChoice(null);
      setCheckedIds(null);
      setExcludedIds(null);
      setJustNormalized(false);
    });
  }, [value, storedExcludedRaw, emptyIsOpenEnded]);

  // #1013: a `required` allow-list (multi, no auto_select) with nothing selected
  // is INVALID, not a sentinel. On an auto_select field an empty value means
  // "extract everything" (#1014), and on an optional allow-list like bing_ads
  // `account_ids` it means the same — but a `required` allow-list whose
  // backend settings model declares `list[str] = Field(..., min_length=1)`
  // PATCHes an empty list cleanly and then fails at RUN time inside the
  // adapter. Nothing enforced the schema's own "Select at least one ..."
  // rule: the
  // wizard's zod builder maps oauth_populated_dropdown to `z.any().optional()`
  // and the detail config editor validates nothing at all. Report the block to
  // the surrounding gate so BOTH submit buttons (wizard Continue, editor Save)
  // stay disabled until the user picks something.
  //
  // Gated on a resolved discovery with at least one option so we never block a
  // field that hasn't fetched yet (that is the pending gate's job) or one whose
  // account genuinely exposes no options — that case renders its own
  // empty state and has nothing the user could select. `readOnly` is excluded
  // too: the user has no way to clear the block.
  //
  // #1077 widens the same block to an `auto_select: "all"` field that is NOT
  // flagged `empty_means_all`. There an empty list is invalid by the backend
  // contract (min_length=1), exactly like the required allow-list case — and `field.required`
  // cannot be the trigger, because most of those fields declare
  // `required=False` in the wizard schema while their settings models require a
  // non-empty list. Flagged fields keep the sentinel meaning and are never
  // blocked.
  const isPinnedEmpty = config?.auto_select === 'all' && config?.multi && !emptyIsOpenEnded;
  // #2190: what the checkbox list shows -- in 'except', every option not
  // excluded.
  const shownSelection = scopeMode === 'except' ? exceptSelection : effectiveSelection;
  // #2218: a required SINGLE-value dropdown (google_ads `customer_id`) with
  // nothing chosen is invalid in EVERY state, including before discovery has
  // run or when OAuth is unavailable (UAT note 92534: the wizard advanced with
  // "Customers: Not set"). Unlike the list cases below it has nothing to wait
  // for, so it is not gated on a resolved fetch. The pending gate still covers
  // an in-flight fetch.
  const isRequiredSingleEmpty =
    field.required &&
    !config?.multi &&
    !config?.auto_select &&
    !readOnly &&
    shownSelection.length === 0;
  const isRequiredEmpty = Boolean(
    isRequiredSingleEmpty ||
      ((isPinnedEmpty ||
        scopeMode === 'selected' ||
        scopeMode === 'except' ||
        (field.required && config?.multi && !config.auto_select)) &&
        !readOnly &&
        hasFetched &&
        !isLoading &&
        options.length > 0 &&
        shownSelection.length === 0),
  );
  useReportSubmitBlocked(field.name, isRequiredEmpty);

  // Prefer the schema's own message so the copy stays owned by the backend
  // field definition (e.g. "Select at least one account"). #1077: the
  // pinned-empty fields carry no `required` validation rule, and "<label> is
  // required" reads oddly for a list, so name the actual constraint.
  const scopeNoun = config?.discovery_label ?? 'account(s)';
  const requiredMessage =
    field.validations.find((r) => r.type === 'required')?.message ??
    (scopeMode === 'selected'
      ? `Select at least one ${scopeNoun}, or choose all ${scopeNoun}.`
      : scopeMode === 'except'
      ? `Leave at least one ${scopeNoun} checked, or choose all ${scopeNoun}.`
      : isPinnedEmpty
      ? `Select at least one ${config?.discovery_label ?? 'account(s)'} to extract.`
      : `${field.label} is required`);

  // Compute the companion field's value from a set of selected primary ids.
  // Reuses the #205 CSV-flatten + dedupe rule: each row's source_key may be a
  // single id or a CSV of ids; the companion is always a flat collection of
  // individual ids (array when multi, CSV string otherwise). Only the rows whose
  // primary value is in `selectedIds` contribute, so deselecting a row drops its
  // companion ids too. #189
  const companionFor = useCallback(
    (selectedIds: string[]): unknown => {
      const cf = config?.companion_field;
      if (!cf || !config) return undefined;
      const sel = new Set(selectedIds);
      const rows = rawItems.filter((r) => sel.has(String(r[config.value_key] ?? '')));
      const ids = rows
        .flatMap((r) => String(r[cf.source_key] ?? '').split(','))
        .map((s) => s.trim())
        .filter(Boolean);
      const unique = Array.from(new Set(ids));
      return config.multi ? unique : unique.join(',');
    },
    [config, rawItems],
  );

  useEffect(() => {
    if (!config?.auto_select) return;
    if (!hasFetched || isLoading || options.length === 0) return;
    if (autoSelectFiredRef.current) return;

    autoSelectFiredRef.current = true;

    // The ref-guard write above is synchronous (refs, unlike state, are safe
    // to write in an effect body), but everything below both sets local state
    // (setAutoSelectLabel/setCheckedIds) AND calls the external onChange /
    // onBatchFieldChange sync -- deferring the whole block to a microtask
    // (mirrors ColumnConfigStep.tsx's rowSelection sync) keeps every setState
    // call out of the effect body's synchronous path, with no observable
    // delay: microtasks flush before the next paint, and `autoSelectFiredRef`
    // is already flipped so a concurrent effect re-run can't double-fire.
    queueMicrotask(() => {
      // Compute the initial selection. In stored-creds edit mode a previously
      // saved subset already lives in `value`; honor it instead of re-applying the
      // auto_select default, so a user's deselections survive a reload and newly
      // discovered accounts are NOT silently re-added (#189). In wizard mode (no
      // saved value, or no stored creds) the default is the auto_select behavior:
      // every id, or the first id.
      let dropdownValue: unknown;
      let selectedIds: string[];
      if (config.auto_select === 'all') {
        // Select ALL options by default. If the backend expects list[str]
        // (multi: true), submit an array; otherwise join with commas for legacy
        // connectors whose backend parses a CSV string.
        const allIds = options.map((o) => o.value);
        const persisted =
          storedCredsMode && Array.isArray(value)
            ? (value as unknown[]).filter((v): v is string => typeof v === 'string')
            : [];
        // #2100: an open-ended field seeds NOTHING checked -- the All-scope
        // radio represents "everything", not a full set of checked boxes.
        const seed = persisted.length > 0 ? persisted : emptyIsOpenEnded ? [] : allIds;
        selectedIds = seed;
        const noun = config.discovery_label ?? 'advertiser profile(s)';
        setAutoSelectLabel(`${options.length} ${noun} discovered`);
        if (config.multi) {
          setCheckedIds(seed);
          if (emptyIsOpenEnded) {
            // #1014: DISPLAY-ONLY on a field where [] is the open-ended sentinel.
            // Seed the checkbox state so the list renders every discovered option
            // checked, but do NOT write anything into form state. An empty stored
            // value means "extract ALL" to those adapters
            // (snapchat_ads_adapter.py:87, linkedin_ads_adapter.py:788, and
            // bing_ads_adapter via resolve_open_ended_ids — #1170) — open-ended,
            // including accounts added
            // later. Committing the enumerated list turned that into a pinned
            // snapshot: the form mounted dirty and any Save (even one aimed at an
            // unrelated field) silently narrowed the connector's scope. Only a
            // real user toggle commits; see commit() in renderInteractiveMulti.
            return;
          }
          // #1077: on a field with no open-ended sentinel, the enumerated list is
          // the ONLY representation of "everything", so the seed must reach form
          // state. Falling through to the commit tail restores that: in edit mode
          // `seed` is the persisted list, which the shallowEqual guard below
          // recognizes as unchanged (no dirty mount — #1014's fix is preserved);
          // in wizard mode there is no persisted value, so without this the field
          // stays `undefined`, splitFormData drops it, and the create POST omits a
          // `min_length=1` setting -> 422.
          dropdownValue = seed;
        } else {
          dropdownValue = seed.join(',');
        }
      } else {
        // auto_select === 'first': default to the first option, unless a saved
        // single value already exists in edit mode.
        const persistedSingle =
          storedCredsMode && typeof value === 'string' && value.length > 0 ? value : null;
        const chosen =
          (persistedSingle && options.find((o) => o.value === persistedSingle)) || options[0];
        selectedIds = [chosen.value];
        setAutoSelectLabel(chosen.label);
        dropdownValue = chosen.value;
      }

      // Apply the dropdown value AND the companion in a single batch update
      // when both are present. Issuing them separately would race against
      // React's deferred re-render: handleBatchFieldChange captures `values`
      // in closure, so a second call after `onChange(...)` would spread the
      // pre-onChange snapshot back over the newly-set dropdown value
      // (#51 — silently dropped the dropdown's own selection on every
      // OAUTH_POPULATED_DROPDOWN with a companion_field).
      //
      // CRITICAL: skip the onChange/onBatchFieldChange when the computed value is
      // byte-for-byte equal to what the form already holds. Without this guard,
      // every form mount on a connected connector marks the field dirty even
      // though the user didn't touch it, and a subsequent Save ships a partial
      // settings PATCH. The backend now deep-merges (#187), but firing
      // unnecessary onChange + dirty marks still surfaces "Edited" chips for
      // fields the user didn't edit. See #187 / #206.
      if (config.companion_field && onBatchFieldChange && rawItems.length > 0) {
        const { target } = config.companion_field;
        const companionValue = companionFor(selectedIds);
        const dropdownChanged = !shallowEqual(dropdownValue, value);
        const companionChanged = !shallowEqual(companionValue, allValues[target]);
        if (dropdownChanged || companionChanged) {
          onBatchFieldChange({
            [field.name]: dropdownValue,
            [target]: companionValue,
          });
        }
      } else if (!shallowEqual(dropdownValue, value)) {
        onChange(dropdownValue);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFetched, isLoading, options]);

  // Interactive multi-select render for any `multi` dropdown. With
  // auto_select="all" the list is seeded all-checked by default (preserving the
  // historical "all" behavior), but the user can deselect, filter, and bulk
  // select/clear before saving so an extract can be scoped to a subset of
  // accounts. #189
  const renderInteractiveMulti = () => {
    // Seed hoisted to `effectiveSelection` in the component body (#1013) so the
    // required-empty gate hook can read it; the semantics are unchanged.
    // #2190: `shownSelection` is that, or the not-excluded set in 'except'.
    const selected = shownSelection;
    const selectedSet = new Set(selected);
    const needle = filter.trim().toLowerCase();
    // #2207: the filter also matches the id, so a same-named account can be
    // found by the id the provider shows.
    const visible = needle
      ? options.filter(
          (o) => o.label.toLowerCase().includes(needle) || o.value.toLowerCase().includes(needle),
        )
      : options;
    const duplicates = duplicateLabelValues(options);

    const commit = (next: string[]) => {
      // #2190: in 'except' a hand edit changes the DENY-list: every discovered
      // option left unchecked, plus stored exclusions not discovered right now
      // (an account briefly missing from discovery stays excluded).
      if (scopeMode === 'except') {
        const checked = new Set(next);
        const offScreen = effectiveExcluded.filter((id) => !optionOrder.includes(id));
        const denied = [...offScreen, ...optionOrder.filter((id) => !checked.has(id))];
        setExcludedIds(denied);
        // Pin the mode: once the last exclusion is re-checked the stored value
        // reads as 'all', and the list must not vanish mid-edit.
        setScopeChoice('except');
        setJustNormalized(false);
        writeValue([], next, denied);
        return;
      }
      setCheckedIds(next);
      // #2100: any hand edit of the checkboxes is an "Only selected" scope.
      if (emptyIsOpenEnded) setScopeChoice('selected');
      // #1028: any hand edit invalidates the just-normalized confirmation.
      setJustNormalized(false);
      // #1014: where [] is the adapters' open-ended sentinel, "every discovered
      // option checked" IS "extract everything" — persist [] instead of an
      // enumerated snapshot, so the connector picks up accounts added later.
      // `checkedIds` keeps the real list, so the UI is unchanged.
      // NOT applied to multi-without-auto_select: those are operator-curated
      // allow-lists (#938) where an all-checked selection is a deliberate
      // enumeration, not a shorthand.
      // #1077: nor to an auto_select field without `empty_means_all`, whose
      // adapter iterates the stored list. Normalizing there produced a value the
      // backend rejects (422 `List should have at least 1 item`), making an
      // all-checked selection unsavable. (#1171: no shipping field is in that
      // state any more -- #1093 flagged the last of them -- so this arm is now
      // a guard for future connectors rather than a live case.)
      //
      // #2100: SUPERSEDED for `empty_means_all` fields. An all-checked selection
      // made by hand is now an enumerated pin like any other; only the explicit
      // All-scope radio (normalizeToOpenEnded) persists []. Normalizing here is what
      // let "clear all" / "check all" flip the connector to open-ended by
      // accident.
      writeValue(next, next, effectiveExcluded.length > 0 ? [] : undefined);
    };
    // Writes the field (and its companion, derived from the rows actually in
    // scope) in one batch -- see the #51 note on the auto-select effect.
    // #2190: `excluded`, when given, is written to the deny-list sibling in the
    // same batch; `undefined` leaves the sibling untouched, so a connector that
    // never used 'except' gains no new settings key.
    const writeValue = (persisted: string[], inScope: string[], excluded?: string[]) => {
      lastWrittenRef.current = persisted;
      const updates: Record<string, unknown> = { [field.name]: persisted };
      if (excludedFieldName && excluded !== undefined) {
        updates[excludedFieldName] = excluded;
        lastWrittenExcludedRef.current = excluded;
      }
      if (config?.companion_field) {
        // Derived from the rows actually in scope — passing `persisted` would
        // wipe the companion on the open-ended [].
        updates[config.companion_field.target] = companionFor(inScope);
      }
      if (Object.keys(updates).length > 1 && onBatchFieldChange) {
        onBatchFieldChange(updates);
      } else {
        onChange(persisted);
      }
    };
    const toggle = (id: string) => {
      const next = new Set(selectedSet);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      // Preserve discovery order for a stable, predictable committed value.
      commit(optionOrder.filter((v) => next.has(v)));
    };

    // Bulk actions act on the currently *visible* (filtered) items, not the full
    // option set, so a typed filter scopes "Select all" / "Clear all" to what the
    // user is actually looking at (#637). With no filter active, `visible` ===
    // `options`, so Select all unions to the full list and Clear all empties it --
    // unchanged from the historical behavior. Both are additive/subtractive over
    // the existing selection so off-filter picks survive a filtered bulk action,
    // and both re-project through optionOrder for a stable committed value.
    const visibleIds = visible.map((o) => o.value);
    const selectAllVisible = () => {
      const next = new Set(selectedSet);
      visibleIds.forEach((id) => next.add(id));
      commit(optionOrder.filter((v) => next.has(v)));
    };
    const clearAllVisible = () => {
      const next = new Set(selectedSet);
      visibleIds.forEach((id) => next.delete(id));
      commit(optionOrder.filter((v) => next.has(v)));
    };

    // #1014: where [] is the adapters' "extract ALL" sentinel, an empty
    // selection cannot mean "select none". Say so rather than letting an
    // all-unchecked list imply the opposite. Not shown for
    // multi-without-auto_select (#938) — those fields carry the equivalent
    // sentence in their own help_text — and, since #1077, not for an
    // auto_select field without `empty_means_all`, where an empty selection is
    // simply invalid; that case is reported through `isRequiredEmpty` instead.
    //
    // #2100: removed as a render -- an empty "Only selected" scope is now
    // invalid (isRequiredEmpty) and "everything" is the All-scope radio.
    const hintNoun = config?.discovery_label ?? 'account(s)';

    // #1028: a connector saved before #1014 carries an ENUMERATED full list in
    // `settings`, so accounts discovered later are silently excluded from every
    // extract. That state is indistinguishable at rest from a deliberate "I
    // really do want exactly these N", so it is never rewritten automatically --
    // the condition is REPORTED and the operator opts in below.
    //
    // Gated on `emptyIsOpenEnded` (#1077), not merely on auto_select="all": the
    // remedy writes [], which is only a legal open-ended value on a flagged
    // field. On an UNFLAGGED auto_select field the adapter iterates the stored
    // list and the settings model declares min_length=1, so offering the remedy
    // there would hand the operator a button that 422s -- and "pinned" is not
    // even a defect there, since the enumerated list is the only valid state.
    //
    // #1171: when #1077 was written that described six real fields
    // (google_analytics, amazon_dsp, twitter_ads, reddit_ads, tiktok_ads,
    // meta_ads). #1093 gave all six ephemeral discovery and flagged them, so
    // the unflagged set is now EMPTY and this gate has no live negative case.
    // Kept as the contract for future connectors, not as live classification.
    //
    // Reads the persisted `value`, NOT `effectiveSelection`/`checkedIds`: the
    // question is what this connector would SAVE, not what is on screen. An
    // open-ended connector (value === []) therefore never matches, even though
    // it renders all-checked.
    const optionIdSet = new Set(optionOrder);
    const storedIds =
      // Only edit mode has a stored value; in the wizard a connector is born []
      // (#1014), so the notice would be meaningless there.
      storedCredsMode && Array.isArray(value)
        ? (value as unknown[]).filter(
            (v): v is string => typeof v === 'string' && optionIdSet.has(v),
          )
        : [];
    // Set size, not array length: `storedIds` is a subset of the option ids by
    // construction, so a matching DISTINCT count proves set equality -- a raw
    // length compare would misfire on a duplicated id (['a','a'] vs ['a','b']).
    // Filtering out ids no longer discovered is deliberate: a connector pinned
    // to [a, b, x] where x is gone IS pinned to everything discoverable today,
    // and normalizing also sheds the dead x.
    const distinctStored = new Set(storedIds).size;
    // #1097: a pin is any NON-EMPTY stored list on a field where [] is the
    // open-ended sentinel. Open-ended ([] -> storedIds empty) never matches; nor
    // does a non-flagged field, a wizard/readOnly/loading render, or a no-option
    // fetch. `distinctStored` is <= options.length by construction (storedIds is
    // filtered to valid option ids above).
    const isPinned =
      emptyIsOpenEnded &&
      !readOnly &&
      hasFetched &&
      !isLoading &&
      options.length > 0 &&
      distinctStored > 0;
    // #1028: exact-full -- the stored set IS everything discoverable today.
    const isPinnedToFullSet = isPinned && distinctStored === options.length;
    // #1097: strict subset -- options discovered today are being excluded now.
    // #1093 made [] a legal discover-everything value on all 10 fields, so the
    // same remedy applies. Fires on a deliberately-narrowed subset too
    // (indistinguishable at rest); the notice is informational + opt-in, so that
    // trade-off is accepted.
    const isStalePin = isPinned && distinctStored < options.length;

    // The ONE path that persists the open-ended [] -- the notices' remedy and,
    // since #2100, the All-scope radio. Shares writeValue() with commit() so the
    // companion field stays derived from every discovered row. Remembers the
    // scoped selection so switching back to "Only selected" restores it. Writes
    // form state only -- the operator saves explicitly, like any other field
    // edit in the config editor.
    // #2190: leaving 'except' clears the deny-list, remembering it for a return.
    const clearExcluded = () => {
      if (effectiveExcluded.length === 0) return undefined;
      lastExcludedRef.current = effectiveExcluded;
      setExcludedIds([]);
      return [];
    };
    const normalizeToOpenEnded = () => {
      if (scopeMode !== 'except' && selected.length > 0) lastScopedRef.current = selected;
      setScopeChoice('all');
      setCheckedIds(null);
      writeValue([], optionOrder, clearExcluded());
      setJustNormalized(true);
    };
    const chooseSelectedScope = () => {
      // #2190: from 'except', keep exactly the boxes on screen -- only accounts
      // added later stop being included.
      const restore = scopeMode === 'except' ? selected : (lastScopedRef.current ?? []);
      const excluded = clearExcluded();
      setScopeChoice('selected');
      setJustNormalized(false);
      setCheckedIds(restore);
      // From the stored open-ended [] nothing is written until the operator
      // checks something; the required-empty gate keeps Save disabled.
      if (restore.length > 0 || excluded !== undefined) writeValue(restore, restore, excluded);
    };
    // #2190: from 'selected' the unchecked boxes become the deny-list, so the
    // screen is unchanged and only accounts added later join. From 'all',
    // restore the last deny-list, else exclude nothing until a box is cleared.
    const chooseExceptScope = () => {
      const denied =
        scopeMode === 'selected'
          ? optionOrder.filter((id) => !selectedSet.has(id))
          : (lastExcludedRef.current ?? []);
      if (scopeMode === 'selected' && selected.length > 0) lastScopedRef.current = selected;
      setScopeChoice('except');
      setJustNormalized(false);
      setCheckedIds(null);
      setExcludedIds(denied);
      if (scopeMode === 'selected' || denied.length > 0) {
        writeValue(
          [],
          optionOrder.filter((id) => !denied.includes(id)),
          denied,
        );
      }
    };
    const onScopeChange = (next: string) => {
      if (next === scopeMode) return;
      if (next === 'all') normalizeToOpenEnded();
      else if (next === 'except') chooseExceptScope();
      else chooseSelectedScope();
    };
    const scopeAllId = `${selectId}-scope-all`;
    const scopeSelectedId = `${selectId}-scope-selected`;
    const scopeExceptId = `${selectId}-scope-except`;

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-1">
          <Label id={selectId}>
            {field.label}
            {field.required && (
              <>
                <span aria-hidden="true" className="text-destructive ml-1">*</span>
                <span className="sr-only">(required)</span>
              </>
            )}
          </Label>
          {scopeMode !== 'all' && (
            <span className="text-xs text-muted-foreground ml-auto">
              {selected.length} of {options.length} selected
            </span>
          )}
        </div>

        {/* #2100: the scope is its own labelled control, so "everything,
            including any added later" can never be reached -- or left -- by
            ticking boxes. */}
        {scopeMode !== null && (
          <RadioGroup
            value={scopeMode}
            onValueChange={onScopeChange}
            disabled={readOnly}
            aria-label={`${field.label} scope`}
            className="gap-1.5"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="all" id={scopeAllId} />
              <Label htmlFor={scopeAllId} className="font-normal cursor-pointer">
                All {hintNoun}, including any added later
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="selected" id={scopeSelectedId} />
              <Label htmlFor={scopeSelectedId} className="font-normal cursor-pointer">
                Only the {hintNoun} selected below
              </Label>
            </div>
            {excludedFieldName && (
              <div className="flex items-center gap-2">
                <RadioGroupItem value="except" id={scopeExceptId} />
                <Label htmlFor={scopeExceptId} className="font-normal cursor-pointer">
                  The {hintNoun} selected below, plus any added later
                </Label>
              </div>
            )}
          </RadioGroup>
        )}

        {scopeMode === 'except' && (
          <p className="text-sm text-muted-foreground">
            Unchecked {hintNoun} are excluded. Any {hintNoun} added later are extracted.
          </p>
        )}

        {scopeMode === 'all' && !justNormalized && (
          <p className="text-sm text-muted-foreground">
            All {options.length} discovered {hintNoun} are extracted, and any added later
            will be too.
          </p>
        )}

        {scopeMode !== 'all' && (
        <>
        {options.length > 8 && (
          <Input
            type="text"
            placeholder="Filter accounts..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            aria-label="Filter accounts"
            className="h-8"
            disabled={readOnly}
          />
        )}

        <div className="flex gap-3 text-xs">
          <button
            type="button"
            className="underline text-muted-foreground hover:text-foreground disabled:opacity-50"
            onClick={selectAllVisible}
            disabled={readOnly}
          >
            Select all
          </button>
          <button
            type="button"
            className="underline text-muted-foreground hover:text-foreground disabled:opacity-50"
            onClick={clearAllVisible}
            disabled={readOnly}
          >
            Clear all
          </button>
        </div>

        <div
          role="group"
          aria-labelledby={selectId}
          aria-invalid={isRequiredEmpty || undefined}
          aria-describedby={
            [isRequiredEmpty ? requiredId : null, field.help_text ? helpId : null]
              .filter(Boolean)
              .join(' ') || undefined
          }
          className={cn(
            'max-h-64 overflow-y-auto space-y-1 rounded-md border p-2',
            isRequiredEmpty && 'border-destructive',
          )}
        >
          {visible.map((o) => {
            const optionId = `${selectId}-option-${o.value}`;
            return (
              <label
                key={o.value}
                htmlFor={optionId}
                className="flex items-center gap-2 cursor-pointer rounded-md p-1.5 hover:bg-accent/50 transition-colors"
              >
                <Checkbox
                  id={optionId}
                  checked={selectedSet.has(o.value)}
                  onCheckedChange={() => toggle(o.value)}
                  disabled={readOnly}
                  aria-label={o.label}
                />
                <span className="text-sm select-none">{o.label}</span>
                {duplicates.has(o.value) && (
                  <span className="text-xs text-muted-foreground select-none font-mono">
                    {o.value}
                  </span>
                )}
              </label>
            );
          })}
        </div>
        </>
        )}

        {/* #1028: the pinned-to-a-snapshot notice and its opt-in remedy. The
            `!justNormalized` guard is load-bearing: a parent that does not
            immediately feed the new value back through `value` would otherwise
            keep rendering the notice after the operator acted. */}
        {isPinnedToFullSet && !justNormalized && (
          <div className="rounded-md border border-warning bg-background p-3 space-y-2">
            <p className="text-sm text-warning">
              Pinned to exactly these {options.length} {hintNoun}. Any discovered later
              will not be extracted.
            </p>
            <button
              type="button"
              className="text-sm underline text-muted-foreground hover:text-foreground"
              onClick={normalizeToOpenEnded}
            >
              Extract everything, including {hintNoun} added later
            </button>
          </div>
        )}

        {/* #1097: a stored list that is a strict SUBSET of the discovered set --
            options exist today that this connector is not extracting. Offers the
            same open-ended [] remedy as the exact-full notice above, via the one
            normalization path (commit(optionOrder) -> #1014 persists []). */}
        {isStalePin && !justNormalized && (
          <div className="rounded-md border border-warning bg-background p-3 space-y-2">
            <p className="text-sm text-warning">
              Extracting {distinctStored} of {options.length} discovered {hintNoun}. The
              other {options.length - distinctStored} &mdash; and any {hintNoun} added
              later &mdash; are not extracted.
            </p>
            <button
              type="button"
              className="text-sm underline text-muted-foreground hover:text-foreground"
              onClick={normalizeToOpenEnded}
            >
              Extract everything, including {hintNoun} added later
            </button>
          </div>
        )}

        {justNormalized && (
          <p className="text-sm text-muted-foreground">
            Open-ended &mdash; all discovered {hintNoun} will be extracted, including any
            added later. Save to apply.
          </p>
        )}

        {/* #1013: a required allow-list with nothing checked. The submit button
            is already disabled via the gate; this says why, at the field. */}
        {isRequiredEmpty && (
          <p id={requiredId} role="alert" className="text-sm text-destructive">
            {requiredMessage}
          </p>
        )}

        {field.help_text && (
          <p id={helpId} className="text-sm text-muted-foreground">
            {field.help_text}
          </p>
        )}

        {error && (
          <p id={errorId} role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    );
  };

  // Always surface fetch errors, regardless of auto_select vs. dropdown mode.
  // The dropdown JSX below previously rendered the error inline, but the
  // auto_select branch swallowed it (#177). Lifting the alert here makes
  // error display global to both render modes.
  if (fetchError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <span id={errorId} role="alert">{fetchError}</span>
        </AlertDescription>
      </Alert>
    );
  }

  // `multi` WITHOUT auto_select — an operator-curated allow-list (bing_ads
  // `account_ids` #511/#938). Nothing is
  // pre-selected, because an empty selection is meaningful ("extract
  // everything"), but the control still has to be a checkbox list: the
  // single-select <Select> at the bottom of this component can only ever commit
  // ONE string, and these fields are `list[str]` on the backend. Before #938
  // these fields fell through to that <Select> and silently could not express a
  // multi-account selection at all. #938
  if (config?.multi && !config.auto_select) {
    if (!dependenciesReady) {
      return (
        <div className="space-y-2">
          <Label id={selectId}>
            {field.label}
            {field.required && (
              <>
                <span aria-hidden="true" className="text-destructive ml-1">*</span>
                <span className="sr-only">(required)</span>
              </>
            )}
          </Label>
          <div className="rounded-md border px-3 py-2 text-sm text-muted-foreground bg-muted/40">
            Complete authentication above to load options
          </div>
          {field.help_text && (
            <p id={helpId} className="text-sm text-muted-foreground">
              {field.help_text}
            </p>
          )}
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-1">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>Discovering accounts...</span>
        </div>
      );
    }

    if (hasFetched && options.length === 0) {
      const noun = config.discovery_label ?? 'account(s)';
      return (
        <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm bg-muted/40 border-muted text-muted-foreground">
          <span>No {noun} discovered.</span>
        </div>
      );
    }

    if (hasFetched) return renderInteractiveMulti();

    // Dependencies are ready but the first fetch hasn't resolved yet.
    return null;
  }

  // When auto_select is configured, the field is no longer fully headless:
  //   - auto_select="all" + multi  -> interactive checkbox multi-select (#189)
  //   - auto_select="first"        -> standard single-select dropdown below,
  //                                   defaulted to the first option (#189)
  //   - auto_select="all" + !multi -> legacy headless summary (CSV backends)
  if (config?.auto_select) {
    // Show a brief loading indicator only while actively fetching
    if (isLoading) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-1">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>Discovering accounts...</span>
        </div>
      );
    }

    // Interactive multi-select: let the user scope the discovered set (#189).
    if (config.auto_select === 'all' && config.multi && hasFetched && options.length > 0) {
      return renderInteractiveMulti();
    }

    // auto_select="first": fall through to the standard single-select dropdown
    // render below. The first option is already committed by the effect; the
    // user may pick a different one. Only fall through once options exist.
    const fallThroughToDropdown =
      config.auto_select === 'first' && hasFetched && options.length > 0;

    if (!fallThroughToDropdown) {
      // Legacy auto_select="all" + non-multi keeps the read-only summary.
      if (autoSelectLabel) {
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm bg-info/10 border-info text-info">
              <span className="font-medium">&#10003;</span>
              <span>{autoSelectLabel}</span>
            </div>
            {config?.auto_select === 'all' && options.length > 0 && (
              <ul className="ml-4 space-y-1 text-sm text-muted-foreground">
                {options.map((opt) => (
                  <li key={opt.value}>• {opt.label}</li>
                ))}
              </ul>
            )}
          </div>
        );
      }
      // Empty response: the fetch resolved with zero items. Without this branch
      // the section renders completely blank with no diagnostic (#177).
      if (hasFetched && options.length === 0) {
        const noun = config.discovery_label ?? 'advertiser profile(s)';
        return (
          <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm bg-muted/40 border-muted text-muted-foreground">
            <span>No {noun} discovered.</span>
          </div>
        );
      }
      // Before fetch or when deps aren't ready: render nothing
      return null;
    }
    // fallThroughToDropdown === true -> continue to the standard <Select> below
  }

  // fetchError is guaranteed null here — the early return above caught it.
  const describedBy = [
    field.help_text ? helpId : null,
    error ? errorId : null,
  ]
    .filter(Boolean)
    .join(' ') || undefined;
  const isEmpty = hasFetched && !isLoading && options.length === 0 && !fetchError;
  const isDisabled = readOnly || !dependenciesReady || isLoading || isEmpty;

  let placeholder = field.placeholder ?? 'Select an option';
  if (!dependenciesReady) {
    placeholder = 'Complete authentication above to load options';
  } else if (isLoading) {
    placeholder = 'Loading options...';
  } else if (isEmpty) {
    placeholder = 'No accessible customers';
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        <Label htmlFor={selectId}>
          {field.label}
          {field.required && (
            <>
              <span aria-hidden="true" className="text-destructive ml-1">*</span>
              <span className="sr-only">(required)</span>
            </>
          )}
        </Label>

        {field.tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle
                  className="h-3.5 w-3.5 text-muted-foreground cursor-help"
                  aria-hidden="true"
                />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs text-sm">{field.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {isLoading && (
          <Loader2
            className="h-3.5 w-3.5 animate-spin text-muted-foreground ml-1"
            aria-label="Loading"
            role="status"
          />
        )}
      </div>

      <Select
        value={currentValue}
        onValueChange={(val) => {
          // Apply dropdown value and companion in a single batch — see
          // the auto_select branch above for why a paired onChange + batch
          // call drops the dropdown's own value (#51).
          if (config?.companion_field && onBatchFieldChange) {
            const idx = options.findIndex((o) => o.value === val);
            if (idx >= 0 && rawItems[idx]) {
              const { target, source_key } = config.companion_field;
              onBatchFieldChange({
                [field.name]: val,
                [target]: String(rawItems[idx][source_key] ?? ''),
              });
              return;
            }
          }
          onChange(val);
        }}
        disabled={isDisabled}
      >
        <SelectTrigger
          id={selectId}
          aria-required={field.required}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          aria-busy={isLoading}
          className={cn(error && 'border-destructive focus:ring-destructive')}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>

        <SelectContent>
          {isEmpty ? (
            <SelectItem key="__empty__" value="__empty__" disabled>
              No accessible customers
            </SelectItem>
          ) : (
            options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>

      {field.help_text && (
        <p id={helpId} className="text-sm text-muted-foreground">
          {field.help_text}
        </p>
      )}

      {error && (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
